import { logEvent } from "firebase/analytics";
import { analytics } from "./firebase";

// ── Bearer token redaction ────────────────────────────────────────────────────
// Firebase ID tokens and other JWTs follow the pattern:
//   Bearer eyJxxx.eyJxxx.xxx
// This regex matches the full token value and replaces it with [REDACTED].
const BEARER_TOKEN_RE =
  /Bearer\s+eyJ[A-Za-z0-9_\-]+\.[A-Za-z0-9_\-]+\.[A-Za-z0-9_\-]*/gi;

export function redactBearerToken(value: string): string {
  return value.replace(BEARER_TOKEN_RE, "Bearer [REDACTED]");
}

type ParamValue = string | number | boolean | null | undefined;
type EventParams = Record<string, ParamValue>;

/** Recursively redact Bearer tokens from all string values in an event params object. */
function redactParams(params: EventParams): EventParams {
  const out: EventParams = {};
  for (const [k, v] of Object.entries(params)) {
    out[k] = typeof v === "string" ? redactBearerToken(v) : v;
  }
  return out;
}

// ── Core logger ───────────────────────────────────────────────────────────────

export function trackEvent(eventName: string, params?: EventParams): void {
  if (!analytics) return;
  try {
    logEvent(analytics, eventName, params ? redactParams(params) : undefined);
  } catch {
    // Fail silently — ad blockers or CSP may prevent analytics
  }
}

// ── Named event helpers ───────────────────────────────────────────────────────

/** Standard GA4 page_view event. Call on every route change. */
export function trackPageView(pagePath: string): void {
  trackEvent("page_view", { page_path: pagePath });
}

/** Standard GA4 login event. */
export function trackLogin(method: "password" | "magic_link"): void {
  trackEvent("login", { method });
}

/** Fired when the user explicitly signs out. */
export function trackSignOut(): void {
  trackEvent("sign_out");
}

/**
 * Fired for every backend API call (success and error).
 * Query strings are stripped from the URL to avoid leaking tokens or PII.
 * Duration is measured from request start to response receipt.
 */
export function trackApiRequest(
  method: string,
  url: string,
  statusCode: number,
  durationMs: number,
): void {
  const path = url.split("?")[0]; // strip query string — never send query params
  trackEvent("api_request", {
    http_method: method.toUpperCase(),
    endpoint: path,
    status_code: statusCode,
    duration_ms: Math.round(durationMs),
  });
}

/** Fired when a new consultation session is started. */
export function trackConsultationStarted(templateId?: string): void {
  trackEvent("consultation_started", { template_id: templateId ?? "none" });
}

/** Fired when a consultation report is saved/edited. */
export function trackReportSaved(consultationId: string): void {
  trackEvent("report_saved", { consultation_id: consultationId });
}
